import type { DateFormatType, DateTimeInputTemplateProps, DateTimeScrollerTemplateProps } from 'beesoft-components';
import { DateScrollerType, DateSelectionType, DateTime } from 'beesoft-components';
import clsx from 'clsx';
import { addDays, addMonths, endOfDay, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { isEqual } from 'lodash';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../../common/hooks/use-test-tag';
import { IconButton } from '../../button/button';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { forceAssert } from '../../common-functions';
import type { TypeOrArray } from '../../common-interfaces';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import OdinIcon from '../../odin-icon/odin-icon.component';

export interface CurrentTimeButton {
  caption?: string;
  className?: string;
  timeZone?: string;
}

export interface FieldValidation {
  validationType: string;
  numberOfDays: number;
}

export type OdinDateTimeValueType = string | Date | Date[];

export interface OdinDateTimeProps extends DynamicFieldComponentProps<OdinDateTimeValueType> {
  useDefaultDate?: boolean;
  currentTimeBtn?: CurrentTimeButton;
  dateSelectionType?: DateSelectionType;
  dateFormatType?: DateFormatType;
  fieldValidations?: FieldValidation;
  allowClear?: boolean;
  useFormattedInput?: boolean;
  showIcon?: boolean;
}

export default function OdinDateTime({
  label,
  value,
  error = '',
  onChange,
  useDefaultDate = false,
  dateSelectionType = DateSelectionType.DateTime,
  dateFormatType,
  readOnly,
  required,
  currentTimeBtn,
  fieldValidations,
  allowClear,
  className,
  useFormattedInput = true,
  showIcon = true,
  labelStyles,
  registerPostSubmitHandler,
}: OdinDateTimeProps) {
  const [valueState, setValueState] = useState(value);

  const postSubmitHandlerRegistered = useRef<boolean>(false);
  const latestValidValueStateRef = useRef<OdinDateTimeValueType | undefined>(valueState);

  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only sync when value prop changes, check state to prevent redundant updates
  useEffect(() => {
    if (!isEqual(valueState, value)) {
      setValueState(value);
      latestValidValueStateRef.current = value;
    }
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: afterSubmitFormHandler is stable, only need to register once
  useEffect(() => {
    if (registerPostSubmitHandler && !postSubmitHandlerRegistered.current) {
      registerPostSubmitHandler(() => afterSubmitFormHandler);
      postSubmitHandlerRegistered.current = true;
    }
  }, [registerPostSubmitHandler]);

  const afterSubmitFormHandler = useCallback((sender: OdinDataSender<void>) => {
    sender.sendFinalData();
    const latestValueState = latestValidValueStateRef.current;
    setValueState(latestValueState);
  }, []);

  const tagCreator = useTestTag(label || '');

  const dateChangeHandler = (newDateSelectionValue?: Date | Array<Date>) => {
    if (dateSelectionType === DateSelectionType.TimeOnly) {
      onChange?.(forceAssert(newDateSelectionValue ?? null));
    } else {
      onChange?.(newDateSelectionValue);
    }
  };

  const handleCurrentTime = () => {
    let currentTime = new Date();
    if (currentTimeBtn && currentTimeBtn.timeZone) {
      currentTime = toZonedTime(currentTime.toISOString(), currentTimeBtn.timeZone);
    }
    setValueState(currentTime);
    latestValidValueStateRef.current = currentTime;
    onChange?.(currentTime);
  };

  const advancedDateValidation = (date: Date, fieldValidations: FieldValidation) => {
    switch (fieldValidations.validationType) {
      case 'DATE_BEFORE_OR_ON_TODAY':
        return date <= endOfDay(new Date());
      case 'DATE_ON_OR_AFTER_TODAY':
        return date >= startOfDay(new Date());
      case 'DATE_TODAY_PLUS_ONE_MONTH':
        return date >= startOfDay(new Date()) && date <= addMonths(new Date(), 1);
      case 'DATE_TODAY_PLUS_CUSTOM_NB_OF_DAYS':
        return date >= startOfDay(new Date()) && date <= addDays(new Date(), fieldValidations.numberOfDays);
      default:
        return true;
    }
  };

  const validateSelectableDates = (date: Date) => {
    if (fieldValidations && dateSelectionType === DateSelectionType.DateOnly) {
      return advancedDateValidation(date, fieldValidations);
    }

    return true;
  };

  const isValidDate = (date: Date) => {
    if (!validateSelectableDates(date)) {
      return false;
    }

    return true;
  };

  const onError = (invalidDate?: OdinDateTimeValueType) => {
    setValueState(invalidDate);
  };

  const inputStyles = clsx(
    'rounded-none appearance-none leading-4 border relative w-full py-1 placeholder-gray-3 text-sm focus:outline-hidden',
    {
      'bg-mono-1 px-2': !readOnly,
      'border-transparent bg-transparent cursor-default px-0':
        readOnly && (valueState !== null || valueState !== '' || valueState !== undefined),
      hidden: readOnly && (valueState === null || valueState === '' || valueState === undefined),
      'border-gray-4': !readOnly && !error,
      'border-error': error,
    },
    className
  );

  const currentTimeBtnStyles = clsx(
    'h-auto border border-gray-3 bg-transparent px-3 text-sm font-medium sm:w-auto',
    currentTimeBtn?.className,
    {
      hidden: readOnly,
    }
  );

  const inputTemplate = (_props: DateTimeInputTemplateProps, children: TypeOrArray<ReactNode>) => (
    <div className="flex">
      {children}
      {currentTimeBtn && (
        <button
          className={currentTimeBtnStyles}
          onClick={handleCurrentTime}
          ref={tagCreator('now-button')}
          type="button"
        >
          {currentTimeBtn?.caption ?? t('now', 'Now')}
        </button>
      )}
    </div>
  );

  const scrollerTemplate = useCallback((props: DateTimeScrollerTemplateProps, children: TypeOrArray<ReactNode>) => {
    const moveToPreviousYear = () => {
      props.decrementViewYears?.(1);
    };

    const moveToNextYear = () => {
      props.incrementViewYears?.(1);
    };

    if (props.scrollerType === DateScrollerType.Day) {
      return (
        <div className="flex w-full flex-row items-center justify-between px-2 py-1">
          <IconButton
            classNames="p-1 hover:bg-gray-5 rounded-sm focus:outline-hidden"
            onClick={moveToPreviousYear}
            icon="ChevronsLeft"
          />
          <IconButton
            classNames="p-1 hover:bg-gray-5 rounded-sm focus:outline-hidden"
            onClick={props.onMovePrevious}
            icon="ChevronLeft"
          />
          <div className="flex-grow cursor-pointer text-center" onClick={props.onTitleClicked}>
            {props.title}
          </div>
          <IconButton
            classNames="p-1 hover:bg-gray-5 rounded-sm focus:outline-hidden"
            onClick={props.onMoveNext}
            icon="ChevronRight"
          />
          <IconButton
            classNames="p-1 hover:bg-gray-5 rounded-sm focus:outline-hidden"
            onClick={moveToNextYear}
            icon="ChevronsRight"
          />
        </div>
      );
    }

    return <div className="flex w-full flex-row items-center px-2 py-1">{children}</div>;
  }, []);

  const getDateInputIcon = () => {
    if (!showIcon) {
      return <></>;
    }

    if (dateSelectionType === DateSelectionType.TimeOnly) {
      return <OdinIcon icon="Clock" className={clsx('w-5 h-5', { 'cursor-pointer': !readOnly })} />;
    }
  };

  return (
    <div className="w-full" ref={tagCreator('date-time')}>
      {label && (
        <div className="flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <DateTime
        value={valueState}
        icon={getDateInputIcon()}
        iconPosition={showIcon && !readOnly ? 1 : 2} // Left = 1, None = 2
        useDefaultDateValue={useDefaultDate}
        useFormattedInput={useFormattedInput}
        closeSelector={true} // this will only work in the DateOnly selector
        inputTemplate={inputTemplate}
        dateScrollerTemplate={scrollerTemplate}
        dateSelection={dateSelectionType as number}
        dateFormat={dateFormatType}
        onChange={dateChangeHandler}
        readOnly={readOnly}
        className={inputStyles}
        selectableDate={validateSelectableDates}
        isValidDate={isValidDate}
        allowClear={allowClear}
        onError={onError}
      />
      {readOnly && (valueState === null || valueState === '' || valueState === undefined) && <EmptyFieldLabel />}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
}
